import Quill from 'quill';
import Core from 'quill/core';

import Toolbar from 'quill/modules/toolbar';
import Snow from 'quill/themes/snow';

import Bold from 'quill/formats/bold';
import Italic from 'quill/formats/italic';
import Header from 'quill/formats/header';
import { $t } from '@/utils';

// Custom Quill Blots.
import ImageEmbed from '@/components/texteditor/advanced/blots/imageEmbed';
import VideoEmbed from '@/components/texteditor/advanced/blots/videoEmbed';
import TweetEmbed from '@/components/texteditor/advanced/blots/tweetEmbed';
import BlockquoteEmbed from '@/components/texteditor/advanced/blots/blockquoteEmbed';
import YoutubeEmbed from '@/components/texteditor/advanced/blots/youtubeEmbed';
import CodeEmbed from '@/components/texteditor/advanced/blots/codeEmbed';
import HTMLEmbed from '@/components/texteditor/advanced/blots/htmlEmbed';
import TextblockEmbed from '@/components/texteditor/advanced/blots/textblockEmbed';
import InstagramEmbed from '@/components/texteditor/advanced/blots/instagramEmbed';
import TiktokEmbed from '@/components/texteditor/advanced/blots/tiktokEmbed';
import FacebookEmbed from '@/components/texteditor/advanced/blots/facebookEmbed';
import PollEmbed from '@/components/texteditor/advanced/blots/pollEmbed';
import PhotogalleryEmbed from '@/components/texteditor/advanced/blots/photogalleryEmbed';
import LiveEmbed from '@/components/texteditor/advanced/blots/liveEmbed';
import AudioEmbed from '@/components/texteditor/advanced/blots/audioEmbed';
import AnchorEmbed from '@/components/texteditor/advanced/blots/anchorEmbed';
import AdvancedLink from '@/components/texteditor/advanced/blots/advancedLinkInline';
import SearchedString from '@/components/texteditor/advanced/blots/searchedString';
import AdvancedLinkModule from '@/components/texteditor/advanced/modules/AdvancedLink';
import AnchorLinkModule from '@/components/texteditor/advanced/modules/AnchorLink';
import DividerBlock from '@/components/texteditor/advanced/blots/dividerBlock';
import ScrollytellingSpacerBlock from '@/components/texteditor/advanced/blots/scrollytellingSpacerBlock';
import ScrollytellingImageEmbed from '@/components/texteditor/advanced/blots/scrollytellingImageEmbed';

const icons = Quill.import('ui/icons');

icons[AnchorEmbed.blotName] = '<i class="fa fa-hashtag"></i>';
const linkIcon = icons.link;
// eslint-disable-next-line
icons.link = '<svg class="svg-inline--fa fa-globe-europe fa-w-16" viewBox="0 0 496 512"><path fill="currentColor" d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm200 248c0 22.5-3.9 44.2-10.8 64.4h-20.3c-4.3 0-8.4-1.7-11.4-4.8l-32-32.6c-4.5-4.6-4.5-12.1.1-16.7l12.5-12.5v-8.7c0-3-1.2-5.9-3.3-8l-9.4-9.4c-2.1-2.1-5-3.3-8-3.3h-16c-6.2 0-11.3-5.1-11.3-11.3 0-3 1.2-5.9 3.3-8l9.4-9.4c2.1-2.1 5-3.3 8-3.3h32c6.2 0 11.3-5.1 11.3-11.3v-9.4c0-6.2-5.1-11.3-11.3-11.3h-36.7c-8.8 0-16 7.2-16 16v4.5c0 6.9-4.4 13-10.9 15.2l-31.6 10.5c-3.3 1.1-5.5 4.1-5.5 7.6v2.2c0 4.4-3.6 8-8 8h-16c-4.4 0-8-3.6-8-8s-3.6-8-8-8H247c-3 0-5.8 1.7-7.2 4.4l-9.4 18.7c-2.7 5.4-8.2 8.8-14.3 8.8H194c-8.8 0-16-7.2-16-16V199c0-4.2 1.7-8.3 4.7-11.3l20.1-20.1c4.6-4.6 7.2-10.9 7.2-17.5 0-3.4 2.2-6.5 5.5-7.6l40-13.3c1.7-.6 3.2-1.5 4.4-2.7l26.8-26.8c2.1-2.1 3.3-5 3.3-8 0-6.2-5.1-11.3-11.3-11.3H258l-16 16v8c0 4.4-3.6 8-8 8h-16c-4.4 0-8-3.6-8-8v-20c0-2.5 1.2-4.9 3.2-6.4l28.9-21.7c1.9-.1 3.8-.3 5.7-.3C358.3 56 448 145.7 448 256zM130.1 149.1c0-3 1.2-5.9 3.3-8l25.4-25.4c2.1-2.1 5-3.3 8-3.3 6.2 0 11.3 5.1 11.3 11.3v16c0 3-1.2 5.9-3.3 8l-9.4 9.4c-2.1 2.1-5 3.3-8 3.3h-16c-6.2 0-11.3-5.1-11.3-11.3zm128 306.4v-7.1c0-8.8-7.2-16-16-16h-20.2c-10.8 0-26.7-5.3-35.4-11.8l-22.2-16.7c-11.5-8.6-18.2-22.1-18.2-36.4v-23.9c0-16 8.4-30.8 22.1-39l42.9-25.7c7.1-4.2 15.2-6.5 23.4-6.5h31.2c10.9 0 21.4 3.9 29.6 10.9l43.2 37.1h18.3c8.5 0 16.6 3.4 22.6 9.4l17.3 17.3c3.4 3.4 8.1 5.3 12.9 5.3H423c-32.4 58.9-93.8 99.5-164.9 103.1z"/></svg>';
// eslint-disable-next-line
icons.spellcheck = '<svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"> <path fill="currentColor"  d="M7.9925 10.25H9.56L5.7275 0.5H4.3325L0.5 10.25H2.0675L2.9075 8H7.1375L7.9925 10.25ZM3.4775 6.5L5.03 2.36L6.5825 6.5H3.4775ZM14.8475 6.9425L8.78 13.01L6.0275 10.25L4.97 11.3075L8.7875 15.125L15.905 8L14.8475 6.9425Z" /></svg>';
icons[AdvancedLink.blotName] = linkIcon;
icons['search-replace'] = '<i class="fa fa-search"></i>';
icons.fullscreen = '<i class="fa fa-arrows-alt"></i>';
icons.divider = '<i class="fa fa-window-minimize"></i>';
icons['grammar-checker'] = icons.spellcheck;

Core.register({
  'modules/toolbar': Toolbar,
  'themes/snow': Snow,
  'formats/bold': Bold,
  'formats/italic': Italic,
  'formats/header': Header,
});

Quill.register(AudioEmbed);
Quill.register(ImageEmbed);
Quill.register(VideoEmbed);
Quill.register(TweetEmbed);
Quill.register(BlockquoteEmbed);
Quill.register(YoutubeEmbed);
Quill.register(CodeEmbed);
Quill.register(HTMLEmbed);
Quill.register(TextblockEmbed);
Quill.register(InstagramEmbed);
Quill.register(TiktokEmbed);
Quill.register(FacebookEmbed);
Quill.register(PollEmbed);
Quill.register(PhotogalleryEmbed);
Quill.register(LiveEmbed);
Quill.register(AnchorEmbed);
Quill.register(AdvancedLink);
Quill.register(SearchedString);
Quill.register('modules/advancedLink', AdvancedLinkModule);
Quill.register('modules/anchorLink', AnchorLinkModule);
Quill.register(DividerBlock);
Quill.register(ScrollytellingSpacerBlock);
Quill.register(ScrollytellingImageEmbed);

const THESAURUS_SUGGESTIONS_BUTTON = 'thesaurus-suggestions';
icons[THESAURUS_SUGGESTIONS_BUTTON] = `<i class="material-icons" style="font-size: 18px" title="${$t('message.editor.button.thesaurusSuggestions')}">book</i>`;

export { Core as default, Quill, THESAURUS_SUGGESTIONS_BUTTON };
